import React from 'react';
import PropTypes from 'prop-types';

import ChevronRight from '@deliveroo/icon-set/react/ChevronRight';
import Modal from '@deliveroo/consumer-component-library/components/Modal';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import Span from '@deliveroo/consumer-component-library/components/Span';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import classnames from 'classnames';
import i18n from '../../utils/i18n';
import getSafe from '../../utils/getSafe';
import { setParams } from '../../utils/home/filters';
import ButtonGroup from '../ButtonGroup';
import HomeFilterOptions from '../HomeFilterOptions';
import HomeIcon from '../HomeIcon';

import styles from './styles.scss';

const styleMap = {
  CLOSED: {
    ui: 'SELECTION_PAGE',
    state: 'COLLAPSED',
    toggleable: true,
  },
  OPEN_INLINE_ALWAYS: {
    ui: 'LIST',
    state: 'EXPANDED',
    toggleable: false,
  },
};

const COLLAPSIBLE_STATE = {
  COLLAPSED: 'COLLAPSED',
  EXPANDED: 'EXPANDED',
};

const getStyles = (type) => styleMap[type] || styleMap.CLOSED;

/**
 * HomeFiltersModal: a modal to apply restaurant list filters on mobile
 */
// eslint-disable-next-line react/no-deprecated
class HomeFiltersModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.stateFromProps(props);
  }

  componentWillReceiveProps(nextProps) {
    this.setState(this.stateFromProps(nextProps));
  }

  setSelectedFilters = (filterOption, filter) => {
    const { selectedFilters } = this.state;
    const newSeletedFiltersState = setParams({
      type: filter.optionsType,
      filterOption,
      selectedFilters,
    });

    this.props.trackTappedFilterOption({
      filterOption,
      filter,
      selected: newSeletedFiltersState.includes(filterOption.target_params.queryParams),
    });

    this.setState({
      selectedFilters: newSeletedFiltersState,
    });
  };

  onDismiss = () => {
    this.setState(this.stateFromProps(this.props));
    this.props.onClose();
  };

  onSubmit = () => {
    this.props.onChange(this.state.selectedFilters);
    this.props.onClose();
  };

  onReset = () => {
    this.setState(
      Object.assign(this.stateFromProps(this.props), {
        selectedFilters: this.props.defaultFilters,
      }),
      () => {
        this.props.onChange(this.state.selectedFilters);
        this.props.onClose();
      },
    );
  };

  toggleSelectionPage = (filter) => {
    const { filterStyles, selectionPage } = this.state;
    const setCollapsedState = (state) =>
      state === COLLAPSIBLE_STATE.COLLAPSED
        ? COLLAPSIBLE_STATE.EXPANDED
        : COLLAPSIBLE_STATE.COLLAPSED;

    // Function to sync the filterStyle state on a user interaction.
    const changeStyleState = (id) => ({
      [id]: {
        ...filterStyles[id],
        state: setCollapsedState(filterStyles[id].state),
      },
    });

    const filterId = filter ? filter.id : selectionPage.data.id;

    if (filterStyles[filterId].toggleable) {
      const newCollapsibleInfo = changeStyleState(filterId);

      if (newCollapsibleInfo[filterId].state === COLLAPSIBLE_STATE.EXPANDED) {
        this.props.trackTappedFilter({ id: filterId, name: filter ? filter.header : '' });
      }

      this.setState({
        filterStyles: {
          ...filterStyles,
          ...newCollapsibleInfo,
        },
        selectionPage: { data: filter }, // If a control type is present then set that as the data to render in the selection page
      });
    }
  };

  // Each control will have it's own styling properties. The collapse enums will map to specific UI, the current states and if those states are toggleable.
  setFilterStyles = (filters) => {
    const filterStyles = {};
    filters.forEach((filter) => {
      filterStyles[filter.id] = getStyles(getSafe(() => filter.styling.web.mobile.collapse));
    });
    return filterStyles;
  };

  stateFromProps = (props) => ({
    filterStyles: this.setFilterStyles(props.filters),
    selectionPage: { data: undefined },
    selectedFilters: props.selectedFilters,
  });

  render() {
    const { isOpen, appElement, filters, trackViewedMobileFilters } = this.props;

    const { selectionPage, selectedFilters } = this.state;

    const renderFooter = () => (
      <ButtonGroup shouldStackOnMobile={false}>
        <Button type="primary" onClick={this.onSubmit}>
          {i18n.gettext('restaurant_list.filters.modal.apply')}
        </Button>

        <Button type="secondary" onClick={this.onReset}>
          {i18n.gettext('restaurant_list.filters.modal.clear')}
        </Button>
      </ButtonGroup>
    );

    const selectedFilterName = (control) => {
      const optionText = control.options
        .filter((opt) => selectedFilters.includes(opt.target_params.queryParams))
        .reduce((str, option, idx) => `${idx > 0 ? `${str}, ${option.name}` : option.name}`, '');
      return optionText || ' ';
    };

    return (
      <Modal
        id="search-modal"
        appElement={appElement}
        isOpen={isOpen}
        onClose={this.onDismiss}
        closeButtonLabel={i18n.gettext('closeButton.label')}
        type="fullscreen"
        headerTitle={
          selectionPage.data
            ? selectionPage.data.header
            : i18n.gettext('restaurant_list.filters.modal.label')
        }
        onBack={selectionPage.data ? () => this.toggleSelectionPage() : null}
        backButtonLabel={i18n.gettext('modal.backButton.label')}
        isContentEdgeToEdge
        renderFooter={renderFooter}
      >
        <div
          className={classnames(styles.wrapper, {
            [styles.selectionPageWrapper]: !!selectionPage.data,
          })}
        >
          {filters.map((filter) => {
            const filterStyles = this.state.filterStyles[filter.id];
            if (selectionPage.data) return null;

            return (
              <div key={filter.id} className={styles.selectionLinkWrapper}>
                {filterStyles &&
                  filterStyles.ui === 'SELECTION_PAGE' && (
                    <React.Fragment>
                      {filterStyles.state === COLLAPSIBLE_STATE.COLLAPSED && (
                        <CollapsedControl
                          onClick={() => this.toggleSelectionPage(filter)}
                          header={filter.header}
                          value={selectedFilterName(filter)}
                          filter={filter}
                          icon={getSafe(() => filter.images.icon) || {}}
                        />
                      )}
                    </React.Fragment>
                  )}

                {filterStyles &&
                  filterStyles.ui === 'LIST' && (
                    <div className={styles.list}>
                      <Heading level={4} spacing="medium medium small medium">
                        {filter.header}
                      </Heading>
                      <HomeFilterOptions
                        id="filter-modal"
                        filter={filter}
                        setSelectedFilters={this.setSelectedFilters}
                        selectedFilters={this.state.selectedFilters}
                        trackViewedMobileFilters={trackViewedMobileFilters}
                        hideHeader
                      />
                    </div>
                  )}
              </div>
            );
          })}

          {selectionPage.data && (
            <div className={styles.list}>
              <HomeFilterOptions
                id="filter-modal"
                filter={selectionPage.data}
                isSelectionPage={!!selectionPage.data}
                setSelectedFilters={this.setSelectedFilters}
                selectedFilters={this.state.selectedFilters}
                trackViewedMobileFilters={trackViewedMobileFilters}
                hideHeader
              />
            </div>
          )}
        </div>
      </Modal>
    );
  }
}

function CollapsedControl({ onClick, header, icon, value }) {
  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
    <div role="button" onClick={onClick} tabIndex={0} className={styles.selectionPageLink}>
      <Span color="black">
        <HomeIcon name={icon.name} spacing="0 small 0 0" color="grey" fallbackUrl={icon.image} />
        {header}
        <Span color="grey" spacing="0 small 0 0">
          {value}
        </Span>
      </Span>
      <Icon component={ChevronRight} color="teal" />
    </div>
  );
}

CollapsedControl.defaultProps = {
  value: ' ',
  icon: {
    name: '',
    image: '',
  },
};

CollapsedControl.propTypes = {
  onClick: PropTypes.func.isRequired,
  header: PropTypes.string.isRequired,
  icon: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
  }),
  value: PropTypes.string,
};

HomeFiltersModal.propTypes = {
  /**
   Selector to specify the portion of your app that should be hidden (via aria-hidden)
   to prevent assistive technologies from reading content outside of this modal.
  */
  appElement: PropTypes.string.isRequired,

  /**
    Whether the modal is open or not
  */
  isOpen: PropTypes.bool,

  /**
    Event handler call when the users closes the modal by clicking on
    the background, pressing escape, or pressing the close icon.
  */
  onClose: PropTypes.func.isRequired,

  /**
    Event handler call when the selected filters have been changed
  */
  onChange: PropTypes.func.isRequired,

  /**
   Method that fires a tracking event when a user clicks on a filter
   */
  trackTappedFilter: PropTypes.func.isRequired,

  /**
    Method that fires a tracking event when a user clicks on a filter option
  */
  trackTappedFilterOption: PropTypes.func.isRequired,

  /**
   Method that fires a tracking event when a user views the mobile filter section
  */
  trackViewedMobileFilters: PropTypes.func.isRequired,

  /** Array of filters to render in the modal */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      images: PropTypes.shape({
        icon: PropTypes.shape({
          image: PropTypes.string,
          name: PropTypes.string,
        }),
      }).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          name: PropTypes.string.isRequired,
          selected: PropTypes.bool,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired,
      ).isRequired,
      styling: PropTypes.shape({
        mobile: PropTypes.shape({
          collapse: PropTypes.string,
          seperators: PropTypes.string,
        }),
        desktop: PropTypes.shape({
          collapse: PropTypes.string,
          seperators: PropTypes.string,
        }),
      }),
      optionsType: PropTypes.string.isRequired,
    }),
  ).isRequired,

  /**
   Array constructed of the currently selected controls.
  */
  // eslint-disable-next-line react/no-unused-prop-types
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,

  /**
   Array constructed of the default controls to fallback to for reset.
  */
  // eslint-disable-next-line react/no-unused-prop-types
  defaultFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
};

HomeFiltersModal.defaultProps = {
  isOpen: false,
};

export default HomeFiltersModal;
